import { useThree } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'
import { Texture } from 'three'

/**
 * A simple cache of already-loaded textures; this cache is global because
 * video memory is shared everywhere within the app.
 */
const loadedTextures = new Map<string, Texture>()

/**
 * Loads a texture into video memory from a specified URL
 * @param url The URL to load a texture from
 */
export const useLoadTexture = (url: string) => {
  const [texture, setTexture] = React.useState<THREE.Texture>()
  const [isLoadingNewTexture, setIsLoadingNewTexture] = React.useState(false)

  const gl = useThree((state) => state.gl)
  React.useEffect(() => {
    if (!url) {
      setIsLoadingNewTexture(true)
      return
    }

    const cachedTexture = loadedTextures.get(url)
    if (cachedTexture) {
      setTexture(cachedTexture)
      setIsLoadingNewTexture(false)
      return
    }

    setIsLoadingNewTexture(true)

    const loader = new THREE.TextureLoader()
    loader
      .loadAsync(url)
      .then((texture) => {
        gl.initTexture(texture)
        loadedTextures.set(url, texture)
        setTexture(texture)
        setIsLoadingNewTexture(false)
      })
      .catch((error) => {
        // TODO ENT-1152: Introduce retries here?
        setTexture(undefined)
        setIsLoadingNewTexture(false)
        console.error(
          `Issue attempting to download texture for "${url}"`,
          error
        )
      })
  }, [url, gl])

  const isLoading = !url || isLoadingNewTexture

  return { texture, isLoading }
}
